import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const formatWon = (n) => `₩${Number(n || 0).toLocaleString('ko-KR')}`;

const PlanSelectionPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [cycle, setCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [plansRes, subRes] = await Promise.allSettled([
          api.get('/billing/plans'),
          api.get('/admin/billing/subscription'),
        ]);
        if (plansRes.status === 'fulfilled') setPlans(plansRes.value.data.plans || []);
        if (subRes.status === 'fulfilled') setCurrent(subRes.value.data.subscription);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSelect = (plan) => setSelected(plan);

  const handleConfirm = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const isChange = current && current.plan_id && current.status !== 'cancelled';
      if (isChange) {
        await api.post('/admin/billing/change-plan', { plan_id: selected.id });
      } else {
        await api.post('/admin/billing/subscribe', { plan_id: selected.id, billing_cycle: cycle });
      }
      navigate('/billing');
    } catch (e) {
      setError(e.response?.data?.error || '구독 처리 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <div className="spinner spinner-dark" style={{ width: 32, height: 32, borderWidth: 3 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Hero */}
      <div style={{ textAlign: 'center', padding: '24px 0 40px' }}>
        <div style={{
          display: 'inline-block',
          padding: '6px 14px',
          background: 'var(--primary-light)',
          color: 'var(--primary)',
          borderRadius: 999,
          fontSize: '0.78rem',
          fontWeight: 700,
          marginBottom: 14,
        }}>
          플랜 선택
        </div>
        <h1 style={{
          fontSize: '2.4rem',
          fontWeight: 800,
          color: 'var(--text)',
          letterSpacing: '-1px',
          marginBottom: 10,
        }}>
          매장에 꼭 맞는 플랜을 선택하세요
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', maxWidth: 540, margin: '0 auto' }}>
          14일 무료 체험, 언제든 변경·해지 가능합니다
        </p>

        {/* Cycle toggle */}
        <div style={{
          display: 'inline-flex',
          marginTop: 28,
          padding: 4,
          background: 'var(--bg)',
          borderRadius: 999,
          border: '1px solid var(--border)',
        }}>
          <button
            onClick={() => setCycle('monthly')}
            style={{
              padding: '10px 24px',
              borderRadius: 999,
              fontWeight: 700,
              fontSize: '0.88rem',
              background: cycle === 'monthly' ? 'var(--surface)' : 'transparent',
              color: cycle === 'monthly' ? 'var(--text)' : 'var(--text-tertiary)',
              boxShadow: cycle === 'monthly' ? 'var(--shadow-sm)' : 'none',
              border: 'none',
            }}
          >
            월간 결제
          </button>
          <button
            onClick={() => setCycle('yearly')}
            style={{
              padding: '10px 24px',
              borderRadius: 999,
              fontWeight: 700,
              fontSize: '0.88rem',
              background: cycle === 'yearly' ? 'var(--surface)' : 'transparent',
              color: cycle === 'yearly' ? 'var(--text)' : 'var(--text-tertiary)',
              boxShadow: cycle === 'yearly' ? 'var(--shadow-sm)' : 'none',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            연간 결제
            <span style={{
              padding: '2px 8px',
              background: 'var(--primary)',
              color: '#fff',
              borderRadius: 999,
              fontSize: '0.68rem',
              fontWeight: 700,
            }}>
              2개월 무료
            </span>
          </button>
        </div>
      </div>

      {/* Plans grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 20,
        marginBottom: 40,
      }}>
        {plans.map((plan) => {
          const isCurrent = current?.plan_id === plan.id && current?.status !== 'cancelled';
          const isFeatured = plan.id === 'plan-pro';
          const price = cycle === 'yearly' ? (plan.yearly_price || 0) : (plan.monthly_price || 0);
          const monthlyEffective = cycle === 'yearly'
            ? Math.round((plan.yearly_price || 0) / 12)
            : plan.monthly_price;
          return (
            <div
              key={plan.id}
              style={{
                position: 'relative',
                background: isFeatured
                  ? 'linear-gradient(180deg, #FBF6E9 0%, #FFFFFF 30%)'
                  : 'var(--surface)',
                border: isFeatured ? '2px solid var(--primary)' : '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                padding: 28,
                boxShadow: isFeatured
                  ? '0 12px 40px rgba(191,155,48,0.15)'
                  : 'var(--shadow-card)',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
            >
              {isFeatured && (
                <div style={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  padding: '4px 14px',
                  background: 'var(--primary)',
                  color: '#fff',
                  borderRadius: 999,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}>
                  가장 인기
                </div>
              )}
              {isCurrent && (
                <div style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  padding: '3px 10px',
                  background: 'var(--success-light)',
                  color: 'var(--success)',
                  borderRadius: 999,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                }}>
                  현재 플랜
                </div>
              )}

              <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                {plan.name}
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginBottom: 18, minHeight: 38 }}>
                {plan.description}
              </div>

              <div style={{ marginBottom: 20 }}>
                {price === 0 ? (
                  <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text)', letterSpacing: '-1px' }}>
                    무료
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text)', letterSpacing: '-1px' }}>
                        {formatWon(monthlyEffective)}
                      </span>
                      <span style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>/ 월</span>
                    </div>
                    {cycle === 'yearly' && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
                        연 {formatWon(plan.yearly_price)} 청구
                      </div>
                    )}
                  </>
                )}
              </div>

              <ul style={{ listStyle: 'none', marginBottom: 24 }}>
                {(plan.features || []).map((feat, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '7px 0',
                    fontSize: '0.86rem',
                    color: 'var(--text-secondary)',
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" style={{ marginTop: 3, flexShrink: 0 }}>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {feat}
                  </li>
                ))}
              </ul>

              <button
                className={isFeatured ? 'btn btn-primary' : 'btn btn-outline'}
                style={{ width: '100%', padding: '12px 16px', fontWeight: 700, fontSize: '0.92rem' }}
                disabled={isCurrent}
                onClick={() => handleSelect(plan)}
              >
                {isCurrent ? '현재 사용 중' : '이 플랜 선택'}
              </button>
            </div>
          );
        })}
      </div>

      {/* FAQ / trust */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 28,
        marginBottom: 24,
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 14 }}>자주 묻는 질문</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
          {[
            { q: '언제든 해지 가능한가요?', a: '네, 언제든 해지할 수 있으며 결제 주기 종료일까지 서비스가 유지됩니다.' },
            { q: '플랜 변경 시 요금은?', a: '업그레이드 시 차액만 정산하며, 다운그레이드는 다음 결제일부터 적용됩니다.' },
            { q: '세금계산서 발행 가능한가요?', a: '네, 결제 완료된 인보이스에 대해 자동으로 발행됩니다.' },
            { q: '데이터는 안전한가요?', a: '모든 데이터는 암호화되어 저장되며 매일 백업됩니다.' },
          ].map((f, i) => (
            <div key={i}>
              <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 4, fontSize: '0.9rem' }}>Q. {f.q}</div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: '0.82rem', lineHeight: 1.6 }}>{f.a}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Confirmation modal */}
      {selected && (
        <div
          onClick={() => !submitting && setSelected(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 'var(--radius-lg)',
              padding: 32,
              width: '100%',
              maxWidth: 480,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 700, marginBottom: 8 }}>
              플랜 확인
            </div>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: 6, letterSpacing: '-0.5px' }}>
              {selected.name}
            </h2>
            <p style={{ color: 'var(--text-tertiary)', fontSize: '0.88rem', marginBottom: 20 }}>
              {selected.description}
            </p>

            <div style={{
              padding: 16,
              background: 'var(--bg)',
              borderRadius: 'var(--radius-sm)',
              marginBottom: 20,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.88rem' }}>결제 주기</span>
                <span style={{ fontWeight: 700 }}>{cycle === 'yearly' ? '연간' : '월간'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.88rem' }}>금액</span>
                <span style={{ fontWeight: 700 }}>
                  {formatWon(cycle === 'yearly' ? selected.yearly_price : selected.monthly_price)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.88rem' }}>VAT (10%)</span>
                <span style={{ fontWeight: 700 }}>
                  {formatWon(Math.round((cycle === 'yearly' ? selected.yearly_price : selected.monthly_price) * 0.1))}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: 10,
                marginTop: 10,
                borderTop: '1px solid var(--border)',
              }}>
                <span style={{ fontWeight: 700 }}>총 결제 금액</span>
                <span style={{ fontWeight: 800, color: 'var(--primary)', fontSize: '1.05rem' }}>
                  {formatWon(Math.round((cycle === 'yearly' ? selected.yearly_price : selected.monthly_price) * 1.1))}
                </span>
              </div>
            </div>

            {error && (
              <div style={{
                padding: 12,
                background: 'var(--danger-light)',
                color: 'var(--danger)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.82rem',
                marginBottom: 16,
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-outline"
                disabled={submitting}
                onClick={() => setSelected(null)}
              >
                취소
              </button>
              <button
                className="btn btn-primary"
                disabled={submitting}
                onClick={handleConfirm}
              >
                {submitting ? '처리 중...' : '지금 가입'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanSelectionPage;
